import { useCallback, useEffect } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import {
  isDraftModeExcludingTemplatesAtom,
  programmaticNavigationExpectedAtom,
  WarningAlertState,
  warningAlertStateAtom,
} from "@zapier/canvas-state";

const defaultWarningAlertState: WarningAlertState = {
  isOpen: false,
  title: "",
  message: "",
  primaryButtonText: "Confirm",
  secondaryButtonText: "Cancel",
  status: "warning",
  onPrimaryAction: () => {},
  onSecondaryAction: undefined,
  isPrimaryLoading: false,
};

export function useUnsavedChangesWarning() {
  const isDraftModeNotTemplates = useAtomValue(
    isDraftModeExcludingTemplatesAtom,
  );
  const store = useStore();
  const setProgrammaticNavExpected = useSetAtom(
    programmaticNavigationExpectedAtom,
  );
  const setWarningAlertState = useSetAtom(warningAlertStateAtom);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      const isExpected = store.get(programmaticNavigationExpectedAtom);

      if (isExpected) {
        setTimeout(() => {
          setProgrammaticNavExpected(false);
        }, 50);
        return;
      }

      event.preventDefault();
      event.returnValue = "";
      return "";
    },
    [store, setProgrammaticNavExpected],
  );

  useEffect(() => {
    if (!isDraftModeNotTemplates) {
      return;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleBeforeUnload, isDraftModeNotTemplates]);

  const closeWarning = useCallback(() => {
    setWarningAlertState(defaultWarningAlertState);
  }, [setWarningAlertState]);

  const requestStartOverWarning = useCallback(
    (confirmAction: () => void | Promise<void>) => {
      if (!isDraftModeNotTemplates) {
        confirmAction();
        return;
      }

      const handlePrimaryAction = async () => {
        setWarningAlertState((prev) => ({ ...prev, isPrimaryLoading: true }));

        setTimeout(async () => {
          try {
            await Promise.resolve(confirmAction());
          } catch {
            setProgrammaticNavExpected(false);
          } finally {
            closeWarning();
          }
        }, 0);
      };

      setWarningAlertState({
        isOpen: true,
        title:
          "You will lose your generated canvas and any progress from this session if you continue.",
        message: "Do you wish to continue?",
        primaryButtonText: "Start from a blank Canvas",
        secondaryButtonText: "Cancel",
        status: "warning",
        onPrimaryAction: handlePrimaryAction,
        onSecondaryAction: closeWarning,
        isPrimaryLoading: false,
      });
    },
    [
      isDraftModeNotTemplates,
      setWarningAlertState,
      closeWarning,
      setProgrammaticNavExpected,
    ],
  );

  return { requestStartOverWarning, closeWarning };
}
